
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { works } from '../data/works';
import './Works.css';

const CATEGORY_ORDER = [
  '看板・サイン',
  'LEDビジョン',
  'LED照明',
  'LED表示設備',
  '電光掲示板',
  '防犯カメラ',
  '駐車場設備',
  'パーキングシステム',
  '緊急出動警報板',
  '金物製作',
  'カスタム金物製作',
];

const Works = () => {
  const [selectedCategory, setSelectedCategory] = useState('すべて');
  const [showAll, setShowAll] = useState(false);

  const projects = useMemo(
    () => (Array.isArray(works) ? works : []),
    []
  );

  const categories = useMemo(() => {
    const existing = [
      ...new Set(
        projects
          .map((project) => project.category)
          .filter(Boolean)
      ),
    ];

    return [
      'すべて',
      ...CATEGORY_ORDER.filter((cat) => existing.includes(cat)),
      ...existing.filter((cat) => !CATEGORY_ORDER.includes(cat)),
    ];
  }, [projects]);

  const filteredProjects = useMemo(() => {
    if (selectedCategory === 'すべて') {
      return projects;
    }

    return projects.filter(
      (project) => project.category === selectedCategory
    );
  }, [projects, selectedCategory]);

  const visibleProjects = showAll
    ? filteredProjects
    : filteredProjects.slice(0, 12);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setShowAll(false);
  };

  return (
    <main className="works-page">
      {/* ===== Page Header ===== */}
      <section className="works-page-hero">
        <div className="works-page-container">
          <span className="works-page-eyebrow">WORKS</span>

          <h1>施工実績</h1>

          <p className="works-page-description">
            看板・サイン、LED表示設備など、
            これまでに手がけた施工事例をご紹介します。
          </p>
        </div>
      </section>

      {/* ===== Project Gallery ===== */}
      <section className="works-page-section">
        <div className="works-page-container">
          <div className="works-page-section-header">
            <div>
              <span className="works-page-eyebrow">
                PROJECT GALLERY
              </span>
              <h2>施工事例一覧</h2>
            </div>

            <p className="works-page-count">
              {filteredProjects.length}件の施工事例
            </p>
          </div>

          {/* Category Filters */}
          <div
            className="works-page-filters"
            aria-label="施工実績のカテゴリー"
          >
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={
                  selectedCategory === category
                    ? 'works-page-filter is-active'
                    : 'works-page-filter'
                }
                aria-pressed={selectedCategory === category}
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Project Cards */}
          <div className="works-page-grid">
            {visibleProjects.map((project) => {
              const image =
                project.thumbnail || project.image || project.full;

              const fullImage =
                project.full || project.image || project.thumbnail;

              return (
                <article
                  key={project.id}
                  className="works-page-card"
                >
                  <a
                    href={fullImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="works-page-image-link"
                    aria-label={`${project.title}の写真を拡大する`}
                  >
                    <div className="works-page-image">
                      <img
                        src={image}
                        alt={project.title}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </a>

                  <div className="works-page-card-body">
                    <span className="works-page-category">
                      {project.category}
                    </span>

                    <h3>{project.title}</h3>

                    {project.description && (
                      <p className="works-page-card-description">
                        {project.description}
                      </p>
                    )}

                    <a
                      href={fullImage}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="works-page-view"
                    >
                      写真を拡大する
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>

          {filteredProjects.length === 0 && (
            <p className="works-page-empty">
              現在、掲載中の施工事例はありません。
            </p>
          )}

          {/* More Button */}
          {filteredProjects.length > 12 && (
            <div className="works-page-more">
              <button
                type="button"
                onClick={() => setShowAll((current) => !current)}
              >
                {showAll ? '表示を少なくする' : 'もっと見る'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ===== Contact ===== */}
      <section className="works-page-contact">
        <div className="works-page-container">
          <div className="works-page-contact-inner">
            <div>
              <span className="works-page-eyebrow">CONTACT</span>
              <h2>製作・施工についてのご相談</h2>
              <p>
                看板・表示設備などの製作や施工について、
                お気軽にお問い合わせください。
              </p>
            </div>

            <Link to="/contact" className="works-page-contact-link">
              お問い合わせ
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Works;
